import { useApolloClient } from "@apollo/client";
import { ArrowUpIcon, XIcon } from "@heroicons/react/outline";
import { Editor } from "@tiptap/core";
import { truncate } from "lodash";
import {
  ChangeEvent,
  ClipboardEvent,
  Dispatch,
  FormEvent,
  SetStateAction,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import {
  GetAskAiChatSessionQueryQuery,
  GetAskAiChatSessionQueryQueryVariables,
} from "types/graphql-schema";
import { v4 as uuidv4 } from "uuid";

import ChatHistoryPopover from "@apps/home/components/chat-history-popover";
import cache, {
  currentOrganizationVar,
  errorNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import AppPopover from "@components/popover/app-popover";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import {
  TopicflowIconType,
  getTopicflowIcon,
} from "@components/topicflow-icon/topicflow-icon-helpers";
import { asyncUploadImage } from "@components/wysiwyg/extensions/image-helpers";
import TextOnlyWysiwyg from "@components/wysiwyg/text-only-wysiwyg";
import { classNames } from "@helpers/css";

import { ChatSessionContext } from "../ask-ai-context";
import getAskAiSessionQuery from "../graphql/get-ask-ai-session-query";
import searchSkillsQuery from "../graphql/search-skills-query";
import { getChatSessionTitle } from "../helpers";
import SkillCommands, { SkillItem } from "../wysiwyg/skill-commands-extension";
import SkillsDialog from "./skills-dialog";

export type AskAIImageAttachment = {
  id: string;
  filename: string;
  previewUrl: string;
  imageUrl?: string;
  isUploading: boolean;
};

const maxAskAIImageAttachments = 4;
const maxAskAIImageAttachmentBytes = 5 * 1024 * 1024;
const supportedImageMimeTypes = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
]);
const supportedImageAcceptAttribute = Array.from(supportedImageMimeTypes).join(
  ",",
);

const AskAIForm = forwardRef(
  (
    {
      chatSessionId,
      inputMessage,
      formButtons,
      loadingCreateMessage,
      loadingRetryAskAiMessage,
      imageAttachments,
      autoFocus = false,
      onSubmit,
      onEditorReady,
      onChangeInputValue,
      onChangeImageAttachments,
      onClickPromptSuggestion,
      onClickChatSessionId,
      showChatHistory = false,
    }: {
      chatSessionId: number | null;
      inputMessage: string;
      imageAttachments: AskAIImageAttachment[];
      autoFocus: boolean;
      loadingCreateMessage: boolean;
      loadingRetryAskAiMessage: boolean;
      onSubmit: (e?: FormEvent<HTMLFormElement>) => void;
      onEditorReady: (editor: Editor) => void;
      onChangeInputValue: (value: string) => void;
      onChangeImageAttachments: Dispatch<
        SetStateAction<AskAIImageAttachment[]>
      >;
      onClickPromptSuggestion: (prompt: string) => void;
      onClickChatSessionId?: (chatSessionId: number | null) => void;
      showChatHistory?: boolean;
      formButtons?: {
        text: string;
        prompt: string;
        icon: TopicflowIconType;
      }[];
    },
    ref: React.Ref<HTMLFormElement>,
  ) => {
    const { meetingId, assessmentId } = useContext(ChatSessionContext);
    const history = useHistory();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isSkillsDialogOpen, setIsSkillsDialogOpen] = useState(false);
    const hasSubmittableContent =
      inputMessage.trim().length > 0 ||
      imageAttachments.some((attachment) => !!attachment.imageUrl);
    const isUploadingImages = imageAttachments.some(
      (attachment) => attachment.isUploading,
    );
    const buttonClassName = classNames(
      "h-6 w-6 flex items-center justify-center rounded-full bg-gray-100 text-gray-500",
      hasSubmittableContent && "bg-blue-link text-white hover:bg-blue-800",
    );
    const currentOrganization = currentOrganizationVar();

    const apollo = useApolloClient();
    const organizationId = currentOrganization?.id ?? null;

    // Fetch the full skill list once per (org) and cache it. Subsequent
    // keystrokes filter in-memory — skills are few and the server round-trip
    // makes the picker feel sluggish otherwise.
    const skillsCacheRef = useRef<{
      orgId: number | null;
      promise: Promise<SkillItem[]>;
    } | null>(null);

    const loadAllSkills = useCallback((): Promise<SkillItem[]> => {
      const cached = skillsCacheRef.current;
      if (cached && cached.orgId === organizationId) {
        return cached.promise;
      }
      const promise = apollo
        .query({
          query: searchSkillsQuery,
          variables: { organizationId },
          fetchPolicy: "no-cache",
        })
        .then((res) =>
          ((res.data?.searchSkills ?? []) as SkillItem[]).filter(
            (s) => s.slug !== "user-observations",
          ),
        )
        .catch(() => [] as SkillItem[]);
      skillsCacheRef.current = { orgId: organizationId, promise };
      return promise;
    }, [apollo, organizationId]);

    const fetchSkillItems = useCallback(
      async ({ query }: { query: string }): Promise<SkillItem[]> => {
        const items = await loadAllSkills();
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter((item) =>
          [item.name, item.slug, item.description].some((field) =>
            (field ?? "").toLowerCase().includes(q),
          ),
        );
      },
      [loadAllSkills],
    );

    const extraExtensions = useMemo(() => {
      return [
        SkillCommands.configure({
          items: fetchSkillItems,
        }),
      ];
    }, [fetchSkillItems]);

    useEffect(() => {
      if (window.Intercom) {
        window.Intercom("update", { hide_default_launcher: true });
      }
      return function cleanup() {
        if (window.Intercom) {
          window.Intercom("update", { hide_default_launcher: false });
        }
      };
    }, []);

    const handleRemoveImageAttachment = (attachmentId: string) => {
      onChangeImageAttachments((attachments) => {
        const attachment = attachments.find(({ id }) => id === attachmentId);
        if (attachment) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
        return attachments.filter(({ id }) => id !== attachmentId);
      });
    };

    const handleClickAddImage = () => {
      fileInputRef.current?.click();
    };

    const uploadImageFiles = (files: File[]) => {
      const imageFiles = files.filter((file) =>
        supportedImageMimeTypes.has(file.type),
      );
      if (imageFiles.length !== files.length) {
        errorNotificationVar({
          title: "Unsupported image format.",
          description: "Use PNG, JPEG, WEBP, or GIF.",
        });
      }
      const uploadableImageFiles = imageFiles.filter(
        (file) => file.size <= maxAskAIImageAttachmentBytes,
      );
      if (uploadableImageFiles.length !== imageFiles.length) {
        errorNotificationVar({
          title: "Images must be 5 MB or smaller.",
        });
      }
      const availableSlots = maxAskAIImageAttachments - imageAttachments.length;
      if (availableSlots <= 0) {
        errorNotificationVar({
          title: `You can upload up to ${maxAskAIImageAttachments} images.`,
        });
        return;
      }
      const filesToUpload = uploadableImageFiles.slice(0, availableSlots);
      if (filesToUpload.length < uploadableImageFiles.length) {
        errorNotificationVar({
          title: `You can upload up to ${maxAskAIImageAttachments} images.`,
        });
      }
      const newAttachments = filesToUpload.map((file) => ({
        id: uuidv4(),
        filename: file.name,
        previewUrl: URL.createObjectURL(file),
        isUploading: true,
      }));
      onChangeImageAttachments((attachments) => [
        ...attachments,
        ...newAttachments,
      ]);
      filesToUpload.forEach((file, index) => {
        const attachmentId = newAttachments[index].id;
        asyncUploadImage({ organizationId: currentOrganization.id })(file)
          .then((imageUrl) => {
            if (!imageUrl) {
              throw new Error("The image could not be uploaded.");
            }
            onChangeImageAttachments((attachments) =>
              attachments.map((attachment) =>
                attachment.id === attachmentId
                  ? { ...attachment, imageUrl, isUploading: false }
                  : attachment,
              ),
            );
          })
          .catch(() => {
            handleRemoveImageAttachment(attachmentId);
          });
      });
    };

    const handleChangeImageInput = (e: ChangeEvent<HTMLInputElement>) => {
      uploadImageFiles(Array.from(e.target.files || []));
      e.target.value = "";
    };

    const handlePasteImages = (e: ClipboardEvent<HTMLDivElement>) => {
      const files = Array.from(e.clipboardData.files || []);
      const hasImageFile = files.some((file) => file.type.startsWith("image/"));
      if (!hasImageFile) {
        return;
      }
      e.preventDefault();
      uploadImageFiles(files);
    };

    const response = cache.readQuery<
      GetAskAiChatSessionQueryQuery,
      GetAskAiChatSessionQueryQueryVariables
    >({
      query: getAskAiSessionQuery,
      variables: { chatSessionId: chatSessionId || 0 },
    });
    const cachedChatSession = response?.chatSession;
    const chatSessionTitle = truncate(getChatSessionTitle(cachedChatSession), {
      length: 30,
    });

    return (
      <form
        className={classNames("border rounded-xl bg-white")}
        onSubmit={onSubmit}
        // Warm the skill cache the first time the user focuses anything in the
        // form (the editor, the + dropdown, etc.) so the `/` picker and the
        // Skills dialog feel instant when they're opened.
        onFocusCapture={() => loadAllSkills()}
        ref={ref}
      >
        <div className="relative" onPaste={handlePasteImages}>
          <TextOnlyWysiwyg
            key={chatSessionId}
            value={inputMessage}
            onChangeValue={onChangeInputValue}
            placeholder={
              meetingId
                ? "Ask anything about this meeting..."
                : assessmentId
                  ? `Message Topicflow AI`
                  : "Ask anything..."
            }
            className={classNames(
              "js-ask-ai-input",
              "w-full border-none focus:ring-0 focus:outline-none p-4 pr-16",
              "pb-1 min-h-16 max-h-48 overflow-y-auto",
            )}
            editable
            autofocus={autoFocus}
            extraExtensions={extraExtensions}
            onEnterPressed={onSubmit}
            onEditorReady={onEditorReady}
          />
        </div>
        {imageAttachments.length > 0 && (
          <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
            {imageAttachments.map((attachment) => (
              <div
                key={attachment.id}
                className="group/image relative h-16 w-16 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-50"
              >
                <a
                  href={attachment.imageUrl || attachment.previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block h-full w-full"
                >
                  <img
                    src={attachment.previewUrl}
                    alt={attachment.filename}
                    className={classNames(
                      "h-full w-full object-cover",
                      attachment.isUploading && "opacity-60",
                    )}
                  />
                </a>
                {attachment.isUploading && (
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5 text-center text-[10px] font-medium text-white">
                    Uploading
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveImageAttachment(attachment.id)}
                  className="absolute right-1 top-1 hidden rounded-full bg-black/70 p-0.5 text-white hover:bg-black group-hover/image:block focus:block"
                  aria-label={`Remove ${attachment.filename}`}
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-between items-center px-4 pb-4">
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={supportedImageAcceptAttribute}
              multiple
              className="hidden"
              onChange={handleChangeImageInput}
            />
            {formButtons &&
              formButtons.map((formButton) => (
                <Button
                  key={formButton.text}
                  onClick={() => {
                    onClickPromptSuggestion(formButton.prompt);
                  }}
                >
                  <TopicflowIcon type="users" className="h-4 w-4" />
                  {formButton.text}
                </Button>
              ))}
            {(() => {
              const imageDisabled =
                imageAttachments.length >= maxAskAIImageAttachments ||
                loadingCreateMessage ||
                loadingRetryAskAiMessage;
              const addOptions: DropdownOptionType[] = [
                {
                  label: "Insert Skill",
                  onClick: () => setIsSkillsDialogOpen(true),
                },
                {
                  label: "Manage Skills",
                  onClick: () => {
                    if (
                      hasSubmittableContent &&
                      !window.confirm(
                        "You have unsaved content. Are you sure you want to leave?",
                      )
                    ) {
                      return;
                    }
                    history.push("/account/ai");
                  },
                },
                { type: "divider" },
                {
                  label: "Upload an image",
                  disabled: imageDisabled,
                  onClick: () => handleClickAddImage(),
                },
              ];
              return (
                <Dropdown options={addOptions} aria-label="Add to message">
                  <Button
                    icon={getTopicflowIcon("plus")}
                    theme={buttonTheme.iconGray}
                    size={ButtonSize.medium}
                    tooltip="Add to message"
                    aria-label="Add to message"
                  />
                </Dropdown>
              );
            })()}

            {(meetingId || assessmentId || showChatHistory) && (
              <ChatHistoryPopover onClickChatSessionId={onClickChatSessionId}>
                {cachedChatSession ? (
                  <AppPopover.Button
                    as={Button}
                    theme={buttonTheme.iconGray}
                    size={ButtonSize.medium}
                  >
                    <TopicflowIcon type="previous" className="h-4 w-4" />
                    {chatSessionTitle}
                  </AppPopover.Button>
                ) : undefined}
              </ChatHistoryPopover>
            )}
          </div>

          <button
            type="submit"
            disabled={
              !hasSubmittableContent ||
              isUploadingImages ||
              loadingCreateMessage ||
              loadingRetryAskAiMessage
            }
            className={buttonClassName}
          >
            <ArrowUpIcon className="h-4 w-4" />
          </button>
        </div>
        <SkillsDialog
          isOpen={isSkillsDialogOpen}
          onClose={() => setIsSkillsDialogOpen(false)}
          organizationId={organizationId}
          onPickSkill={(slug) => onClickPromptSuggestion(`/${slug} `)}
        />
      </form>
    );
  },
);

export default AskAIForm;
